import React from 'react';
import { Check, Upload, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import type { BoardColumn } from './boardColumns';
import {
  CHAMPS_FIXES, MappingTarget, FichierReservationsLu, MappingResultat,
  appliquerMappingReservations, lettreColonneReservation, lireFichierReservations, proposerMapping,
} from './reservationImportParser';
import type { ReservationImportResult } from './useReservationImport';

/**
 * Assistant d'import de réservations (Service d'exception) — même patron à trois étapes que
 * ConvivesImportModal : 1 choisir le fichier, 2 associer les colonnes, 3 le résultat. Le fichier est lu
 * dans le navigateur ; seules les lignes nettoyées partent au serveur.
 *
 * Différence avec Convives : chaque tableau a ses propres colonnes, configurées librement par
 * l'utilisateur (BoardColumnsModal). Une colonne du fichier qui ne correspond à rien de connu n'est
 * JAMAIS ignorée par défaut — elle propose de devenir une nouvelle colonne du tableau, pour qu'une
 * donnée du fichier ne disparaisse jamais silencieusement (demande d'Emeni, 25/09).
 */
export function ReservationImportModal({ boardId, boardColumns, importing, onImport, onClose }: {
  boardId: number;
  boardColumns: BoardColumn[];
  importing: boolean;
  onImport: (rows: any[], nouvellesColonnes: BoardColumn[]) => Promise<ReservationImportResult>;
  onClose: () => void;
}) {
  const [lu, setLu] = React.useState<FichierReservationsLu | null>(null);
  const [mapping, setMapping] = React.useState<Record<string, MappingTarget>>({});
  const [erreur, setErreur] = React.useState('');
  const [lecture, setLecture] = React.useState(false);
  const [resultat, setResultat] = React.useState<ReservationImportResult | null>(null);

  const etape = resultat ? 3 : lu ? 2 : 1;
  const editableColumns = boardColumns.filter((c) => !c.locked && !c.field);

  const choisir = async (file: File | null) => {
    if (!file) return;
    setErreur('');
    setLecture(true);
    try {
      const f = await lireFichierReservations(file);
      setLu(f);
      setMapping(proposerMapping(f.columns, boardColumns));
    } catch (e: any) {
      setErreur(e?.message || 'Impossible de lire ce fichier.');
    } finally {
      setLecture(false);
    }
  };

  const application: MappingResultat = React.useMemo(
    () => lu ? appliquerMappingReservations(lu, mapping, boardId, boardColumns) : { rows: [], nouvellesColonnes: [] },
    [lu, mapping, boardId, boardColumns],
  );
  const retenues = application.rows.filter((r) => r.lastName || r.firstName);
  const sansNom = lu ? lu.rows.length - retenues.length : 0;
  const nomManquant = !!lu && !lu.columns.some((k) => mapping[k] === 'lastName' || mapping[k] === 'firstName' || mapping[k] === 'fullName');
  const nbNouvellesColonnes = application.nouvellesColonnes.length;

  const importer = async () => {
    setErreur('');
    try {
      setResultat(await onImport(retenues, application.nouvellesColonnes));
    } catch (e: any) {
      setErreur(e?.message || "Erreur lors de l'import.");
    }
  };

  const sousTitre = etape === 1 ? 'Étape 1 sur 3 — choisir le fichier'
    : etape === 2 ? 'Étape 2 sur 3 — associer les colonnes'
    : 'Étape 3 sur 3 — terminé';

  return (
    <Modal open onClose={onClose} size="md" title="Importer des réservations" subtitle={sousTitre}
      footerLayout="split"
      footer={
        etape === 1 ? (
          <>
            <span />
            <button type="button" onClick={onClose} className={pillSecondary()}>Annuler</button>
          </>
        ) : etape === 2 ? (
          <>
            <button type="button" onClick={() => { setLu(null); setErreur(''); }} className={pillSecondary()} disabled={importing}>Retour</button>
            <button type="button" onClick={importer} className={pillPrimary()} disabled={importing || nomManquant || retenues.length === 0}>
              {importing ? <Loader2 size={13} className="animate-spin" /> : null}
              Importer {retenues.length} réservation{retenues.length > 1 ? 's' : ''}
            </button>
          </>
        ) : (
          <>
            <span />
            <button type="button" onClick={onClose} className={pillPrimary()}>Fermer</button>
          </>
        )
      }>
      {etape === 1 && (
        <div className="space-y-3.5">
          <label className="block cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0] || null; e.target.value = ''; choisir(f); }} />
            <div className="border border-dashed border-border rounded-[10px] p-6 flex flex-col items-center justify-center gap-1.5 hover:border-content hover:bg-surface-soft transition-colors">
              {lecture ? <Loader2 size={22} className="animate-spin text-content-muted" /> : <Upload size={22} className="text-content-muted" />}
              <p className="text-[12.5px] font-semibold text-content">Choisir le fichier (Zenchef ou autre)</p>
              <p className="text-[11.5px] text-content-muted">CSV ou Excel — les colonnes s'associent à l'étape suivante</p>
            </div>
          </label>
          <p className="text-[12px] leading-[1.5] text-content-muted text-pretty">
            Aucune colonne du fichier n'est perdue : une colonne reconnue (nom, téléphone, heure...) se range
            au bon endroit, et toute autre colonne propose de devenir une nouvelle colonne du tableau.
          </p>
          {erreur && <p className="text-[12px] text-state-danger font-semibold">{erreur}</p>}
        </div>
      )}

      {etape === 2 && lu && (
        <div className="space-y-3.5">
          <div className="flex items-center gap-[11px] px-[13px] py-[11px] rounded-[10px] bg-surface-soft border border-border-soft">
            <span className="w-7 h-7 shrink-0 rounded-[7px] bg-state-ok-soft border border-state-ok/30 text-state-ok flex items-center justify-center"><Check size={14} /></span>
            <div className="min-w-0">
              <div className="font-mono text-[12px] font-medium text-content truncate">{lu.fileName}</div>
              <div className="text-[11.5px] text-content-muted">{lu.rows.length} ligne{lu.rows.length > 1 ? 's' : ''} lue{lu.rows.length > 1 ? 's' : ''} · {lu.columns.length} colonne{lu.columns.length > 1 ? 's' : ''} détectée{lu.columns.length > 1 ? 's' : ''}</div>
            </div>
          </div>

          {nbNouvellesColonnes > 0 && (
            <div className="flex items-start gap-2.5 px-[13px] py-[11px] rounded-[10px] bg-state-info-soft border border-state-info/30">
              <Sparkles size={15} className="text-state-info shrink-0 mt-px" />
              <span className="text-[12px] leading-relaxed text-state-info">
                {nbNouvellesColonnes} nouvelle{nbNouvellesColonnes > 1 ? 's' : ''} colonne{nbNouvellesColonnes > 1 ? 's' : ''} seront ajoutées au tableau : {application.nouvellesColonnes.map((c) => c.label).join(', ')}.
                Choisissez « Ignorer cette colonne » ci-dessous pour ne pas en créer une.
              </span>
            </div>
          )}

          <div className="space-y-[9px]">
            {lu.columns.map((col, i) => (
              <div key={col} className="grid grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)] items-center gap-[9px]">
                <span className="h-9 px-[11px] flex items-center rounded-[9px] bg-surface-soft border border-border font-mono text-[12px] text-content-muted min-w-0 truncate" title={col}>
                  Colonne {lettreColonneReservation(i)} — « {col.replace(/^﻿/, '')} »
                </span>
                <ArrowRight size={15} className="text-content-muted shrink-0" />
                <Select value={mapping[col]} onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value as MappingTarget }))}
                  className={`!h-9 font-semibold ${mapping[col] === 'new' ? '!border-state-info/40 !text-state-info' : mapping[col] === 'ignore' ? '!border-state-warn/40 !text-state-warn' : ''}`}>
                  <optgroup label="Champs de la réservation">
                    {CHAMPS_FIXES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </optgroup>
                  {editableColumns.length > 0 && (
                    <optgroup label="Colonnes déjà sur ce tableau">
                      {editableColumns.map((c) => <option key={c.id} value={`existing:${c.id}`}>{c.label}</option>)}
                    </optgroup>
                  )}
                  <option value="new">+ Créer une nouvelle colonne « {col.replace(/^﻿/, '')} »</option>
                  <option value="ignore">Ignorer cette colonne</option>
                </Select>
              </div>
            ))}
          </div>

          <p className="text-[12px] leading-[1.5] text-content-muted text-pretty">
            Les lignes sans nom seront écartées{sansNom > 0 ? ` (${sansNom} dans ce fichier)` : ''}. Une
            réservation déjà importée (même référence) n'est jamais dupliquée : elle sera ignorée si le
            fichier est déposé une seconde fois.
          </p>
          {nomManquant && <p className="text-[12px] text-state-warn font-semibold">Indiquez au moins la colonne du nom, du prénom, ou du nom complet.</p>}
          {erreur && <p className="text-[12px] text-state-danger font-semibold">{erreur}</p>}
        </div>
      )}

      {etape === 3 && resultat && (
        <div className="space-y-2">
          <div className="flex items-center gap-[11px] px-[13px] py-[11px] rounded-[10px] bg-state-ok-soft border border-state-ok/30">
            <span className="w-7 h-7 shrink-0 rounded-[7px] bg-surface border border-state-ok/30 text-state-ok flex items-center justify-center"><Check size={14} /></span>
            <div className="text-[13px] font-bold text-state-ok">{resultat.imported} réservation{resultat.imported > 1 ? 's' : ''} importée{resultat.imported > 1 ? 's' : ''}</div>
          </div>
          {(resultat.duplicates_skipped > 0 || resultat.invalid_skipped > 0) && (
            <p className="text-[12px] text-content-muted">
              {resultat.duplicates_skipped > 0 && `${resultat.duplicates_skipped} déjà importée${resultat.duplicates_skipped > 1 ? 's' : ''} ignorée${resultat.duplicates_skipped > 1 ? 's' : ''}`}
              {resultat.duplicates_skipped > 0 && resultat.invalid_skipped > 0 && ' · '}
              {resultat.invalid_skipped > 0 && `${resultat.invalid_skipped} ligne${resultat.invalid_skipped > 1 ? 's' : ''} sans nom écartée${resultat.invalid_skipped > 1 ? 's' : ''}`}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
